import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { EventMarketDto } from '@/api/queries';
import {
  detectOddsDirection,
  mergeOddsPatches,
  outcomeFlashKey,
  readOutcomeValue,
} from '@/domain/live-odds';
import type { LiveEventStatisticsPatch } from '@/domain/live-statistics';
import type { OddsFlash } from '@/ui/common/odds-button';

const FLASH_DURATION_MS = 5_000;

export type LiveEventMetaPatch = {
  active?: boolean;
  marketsCount?: number;
  expireOdds?: string;
};

/** Immutable per-event snapshot; replaced (never mutated) whenever the event changes. */
export type LiveEventState = {
  statisticsPatch?: LiveEventStatisticsPatch;
  oddsPatch?: EventMarketDto[];
  eventMetaPatch?: LiveEventMetaPatch;
  flashes?: ReadonlyMap<string, OddsFlash>;
  /** True after `LiveUpdateFinish` / `Result` — match left the live catalog. */
  finished?: boolean;
};

type DirtyListener = (dirtyEventIds: ReadonlySet<number>) => void;

const EMPTY_FLASHES: ReadonlyMap<string, OddsFlash> = new Map();

function scheduleFrame(callback: () => void): void {
  if (typeof requestAnimationFrame === 'function' && document.visibilityState === 'visible') {
    requestAnimationFrame(callback);
  } else {
    setTimeout(callback, 16);
  }
}

/**
 * Ref-backed live feed store. Socket patches are staged and committed at most
 * once per animation frame; only listeners for the changed events are notified.
 */
export class LiveFeedStore {
  private committed = new Map<number, LiveEventState>();
  private pending = new Map<number, LiveEventState>();
  private eventListeners = new Map<number, Set<() => void>>();
  private dirtyListeners = new Set<DirtyListener>();
  private flushScheduled = false;
  private flashTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  private latest(eventId: number): LiveEventState | undefined {
    return this.pending.get(eventId) ?? this.committed.get(eventId);
  }

  private stage(eventId: number, update: (current: LiveEventState) => LiveEventState): void {
    this.pending.set(eventId, update(this.latest(eventId) ?? {}));
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    scheduleFrame(this.flush);
  }

  flush = (): void => {
    this.flushScheduled = false;
    if (this.pending.size === 0) return;
    const dirty = new Set<number>();
    for (const [eventId, state] of this.pending) {
      this.committed.set(eventId, state);
      dirty.add(eventId);
    }
    this.pending.clear();
    for (const eventId of dirty) {
      const listeners = this.eventListeners.get(eventId);
      if (listeners) for (const listener of listeners) listener();
    }
    for (const listener of this.dirtyListeners) listener(dirty);
  };

  getEventState = (eventId: number): LiveEventState | undefined => this.committed.get(eventId);

  getStatisticsPatch = (eventId: number) => this.committed.get(eventId)?.statisticsPatch;

  getOddsPatch = (eventId: number) => this.committed.get(eventId)?.oddsPatch;

  getEventMetaPatch = (eventId: number) => this.committed.get(eventId)?.eventMetaPatch;

  getOutcomeFlash = (flashKey: string): OddsFlash | undefined => {
    const eventId = Number.parseInt(flashKey, 10);
    return this.committed.get(eventId)?.flashes?.get(flashKey);
  };

  subscribeEvent = (eventId: number, listener: () => void): (() => void) => {
    let listeners = this.eventListeners.get(eventId);
    if (!listeners) {
      listeners = new Set();
      this.eventListeners.set(eventId, listeners);
    }
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) this.eventListeners.delete(eventId);
    };
  };

  /** Called once per committed frame with every event id that changed. */
  subscribe = (listener: DirtyListener): (() => void) => {
    this.dirtyListeners.add(listener);
    return () => {
      this.dirtyListeners.delete(listener);
    };
  };

  patchEventStatistics = (eventId: number, patch: LiveEventStatisticsPatch): void => {
    if (this.latest(eventId)?.finished) return;
    this.stage(eventId, (current) => {
      if (current.finished) return current;
      return {
        ...current,
        statisticsPatch: { ...current.statisticsPatch, ...patch },
      };
    });
  };

  patchEventOdds = (
    eventId: number,
    incoming: EventMarketDto[],
    meta?: LiveEventMetaPatch,
  ): void => {
    if (this.latest(eventId)?.finished) return;

    const previous = this.latest(eventId)?.oddsPatch;
    const nextFlashes = new Map<string, OddsFlash>();

    for (const incomingMarket of incoming) {
      for (const line of incomingMarket.Lines ?? []) {
        for (const odd of line.Odds ?? []) {
          const prevValue = readOutcomeValue(
            previous,
            incomingMarket.Key,
            line.Specifier,
            odd.Outcome,
          );
          const direction =
            prevValue !== undefined ? detectOddsDirection(prevValue, odd.Value) : undefined;
          if (direction) {
            nextFlashes.set(
              outcomeFlashKey(eventId, incomingMarket.Key, line.Specifier, odd.Outcome),
              direction,
            );
          }
        }
      }
    }

    const merged = mergeOddsPatches(previous, incoming);

    this.stage(eventId, (current) => {
      if (current.finished) return current;
      let flashes = current.flashes;
      if (nextFlashes.size > 0) {
        const next = new Map(flashes ?? EMPTY_FLASHES);
        for (const [flashKey, direction] of nextFlashes) {
          next.set(flashKey, direction);
          this.scheduleFlashClear(eventId, flashKey);
        }
        flashes = next;
      }
      return {
        ...current,
        oddsPatch: merged,
        eventMetaPatch: meta ? { ...current.eventMetaPatch, ...meta } : current.eventMetaPatch,
        flashes,
      };
    });
  };

  /** Match finished / left live — stop accepting odds and mark inactive. */
  markEventFinished = (eventId: number): void => {
    this.stage(eventId, (current) => ({
      ...current,
      finished: true,
      eventMetaPatch: { ...current.eventMetaPatch, active: false },
      flashes: EMPTY_FLASHES,
    }));
  };

  private scheduleFlashClear(eventId: number, flashKey: string): void {
    const existing = this.flashTimeouts.get(flashKey);
    if (existing) clearTimeout(existing);
    const timeout = setTimeout(() => {
      this.flashTimeouts.delete(flashKey);
      this.stage(eventId, (current) => {
        if (!current.flashes?.has(flashKey)) return current;
        const next = new Map(current.flashes);
        next.delete(flashKey);
        return { ...current, flashes: next };
      });
    }, FLASH_DURATION_MS);
    this.flashTimeouts.set(flashKey, timeout);
  }

  dispose(): void {
    for (const timeout of this.flashTimeouts.values()) clearTimeout(timeout);
    this.flashTimeouts.clear();
  }
}

const LiveFeedContext = createContext<LiveFeedStore | null>(null);

export function LiveFeedProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new LiveFeedStore());

  useEffect(() => () => store.dispose(), [store]);

  return <LiveFeedContext.Provider value={store}>{children}</LiveFeedContext.Provider>;
}

/** Stable store handle; reading getters from it does not subscribe to updates. */
export function useLiveFeed(): LiveFeedStore {
  const context = useContext(LiveFeedContext);
  if (!context) {
    throw new Error('useLiveFeed must be used within LiveFeedProvider');
  }
  return context;
}

/** Safe variant for components that may render outside the live feed tree. */
export function useOptionalLiveFeed(): LiveFeedStore | null {
  return useContext(LiveFeedContext);
}

const noopSubscribe = () => () => {};
const noopSnapshot = () => undefined;

/** Re-renders only when this event's live state is committed. */
export function useLiveEventState(eventId: number | null | undefined): LiveEventState | undefined {
  const store = useOptionalLiveFeed();
  const id = eventId ?? null;
  const subscribe = useCallback(
    (listener: () => void) =>
      store && id !== null ? store.subscribeEvent(id, listener) : noopSubscribe(),
    [id, store],
  );
  const getSnapshot = useCallback(
    () => (store && id !== null ? store.getEventState(id) : undefined),
    [id, store],
  );
  return useSyncExternalStore(subscribe, getSnapshot, noopSnapshot);
}

/**
 * Revision counter that bumps when any of `eventIds` changes (or any event when
 * `eventIds` is omitted). Use to re-derive values that read several events.
 */
export function useLiveFeedRevision(eventIds?: readonly number[]): number {
  const store = useOptionalLiveFeed();
  const [revision, setRevision] = useState(0);
  const idsKey = eventIds ? eventIds.join(',') : null;

  useEffect(() => {
    if (!store) return;
    const watched = idsKey === null ? null : new Set(idsKey ? idsKey.split(',').map(Number) : []);
    if (watched && watched.size === 0) return;
    return store.subscribe((dirty) => {
      if (watched) {
        let hit = false;
        for (const id of dirty) {
          if (watched.has(id)) {
            hit = true;
            break;
          }
        }
        if (!hit) return;
      }
      setRevision((value) => value + 1);
    });
  }, [idsKey, store]);

  return revision;
}
